import { defineNuxtPlugin, useRuntimeConfig } from '#app'
import Rollbar from 'rollbar'
import { z } from 'zod'
import { httpStatusOf, isUncaughtNotFound } from './http-status'
import { IGNORED_MESSAGES } from './ignored-messages'

type RollbarLogger = {
  error: (...args: Parameters<Rollbar['error']>) => void
  info: (...args: Parameters<Rollbar['info']>) => void
  log: (...args: Parameters<Rollbar['log']>) => void
}

const isNotFound = (error: unknown) => httpStatusOf(error) === 404

// Vue passes the code's error-reference URL in production, its label in dev.
const isComponentUpdate = (info: string) =>
  info === 'component update' || info.endsWith('#runtime-15')

const isTranslationPatchFailure = (err: unknown, info: string) =>
  isComponentUpdate(info) &&
  err instanceof Error &&
  err.name === 'NotFoundError' &&
  /\b(insertBefore|removeChild)\b/.test(err.message)

const isTranslatedPage = () => {
  const classes = document.documentElement.classList
  return classes.contains('translated-ltr') || classes.contains('translated-rtl')
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()

  const publicConfigSchema = z.object({
    ROLLBAR_TOKEN: z.preprocess((v) => (v === '' ? undefined : v), z.string().min(1).optional()),
    ENVIRONMENT: z.string().default(''),
    ROLLBAR_ENVIRONMENT: z.string().default(''),
    ROLLBAR_CODE_VERSION: z.string().default(''),
  })

  const publicConfig = publicConfigSchema.parse(config.public)

  // A dev server would report into the live project.
  const rollbar: RollbarLogger = publicConfig.ROLLBAR_TOKEN && !import.meta.dev
    ? new Rollbar({
        accessToken: publicConfig.ROLLBAR_TOKEN,
        captureUncaught: true,
        captureUnhandledRejections: true,
        checkIgnore: isUncaughtNotFound,
        ignoredMessages: IGNORED_MESSAGES,
        payload: {
          environment: publicConfig.ROLLBAR_ENVIRONMENT || publicConfig.ENVIRONMENT,
          code_version: publicConfig.ROLLBAR_CODE_VERSION,
          client: {
            javascript: {
              source_map_enabled: true,
              code_version: publicConfig.ROLLBAR_CODE_VERSION,
              guess_uncaught_frames: true,
            },
          },
        },
      })
    : {
        error: () => undefined,
        info: () => undefined,
        log: () => undefined,
      }

  nuxtApp.vueApp.config.errorHandler = (err, instance, info) => {
    // A page answers 404 by throwing `createError({ statusCode: 404 })`, so a
    // rendered "not found" arrives here looking like a crash — the outcome, not a
    // failure. Exact 404, unlike the SSR plugin's sub-500: a 403 here is a staff
    // preview whose credentials were refused, which is worth reporting.
    // Still logged: a 404 that is a defect should leave a trace in `pnpm dev`.
    console.error('[Vue error]', err)
    if (isNotFound(err)) return
    // Chrome's page translation swaps text nodes for <font> wrappers Vue still
    // holds references to, so its next patch throws NotFoundError. Not ours to fix.
    if (isTranslationPatchFailure(err, info) && isTranslatedPage()) return

    rollbar.error(err as Error, { vueInfo: info })
  }

  return {
    provide: {
      rollbar,
    },
  }
})
